import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, MessageSquare, Mail, Send, Trash2, DollarSign, ExternalLink } from "lucide-react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import PaymentLinkShareDialog from "./PaymentLinkShareDialog";

// iter93/94 — Admin dashboard tab: bookings that have not completed Stripe
// checkout yet. They're hidden from the calendar / today / route / crew
// schedule / revenue reports until Stripe confirms payment (backend filter
// _PAID_STATUS_MATCH). This panel surfaces them with the merchant-approved
// action buttons: Send Payment Reminder, Send Payment Link, Text, Email,
// Call, Delete.
export default function PendingPaymentsPanel() {
  const [state, setState] = useState({ loading: true, bookings: [], count: 0, total_due: 0 });
  const [busyId, setBusyId] = useState(null);
  const [shareDialog, setShareDialog] = useState({ open: false, data: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/pending-payments");
      setState({ loading: false, bookings: data.bookings || [], count: data.count || 0, total_due: data.total_due || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load pending payments");
      setState({ loading: false, bookings: [], count: 0, total_due: 0 });
    }
  };

  useEffect(() => { load(); }, []);

  const sendReminder = async (b) => {
    setBusyId(b.booking_id);
    try {
      const { data } = await api.post(`/admin/pending-payments/${b.booking_id}/send-reminder`);
      const parts = [];
      if (data.sent?.sms) parts.push("SMS");
      if (data.sent?.email) parts.push("email");
      toast.success(parts.length ? `Reminder sent via ${parts.join(" + ")}` : "Reminder queued");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reminder failed");
    } finally { setBusyId(null); }
  };

  const sendLink = async (b) => {
    setBusyId(b.booking_id);
    try {
      const { data } = await api.post(`/admin/pending-payments/${b.booking_id}/send-link`);
      // iter132c — Copy immediately + open the quick-share dialog with
      // native share / SMS / email / open payment page actions.
      if (data.checkout_url) {
        try {
          await navigator.clipboard.writeText(data.checkout_url);
        } catch {
          /* clipboard may be blocked in some browsers — dialog handles fallback */
        }
        setShareDialog({
          open: true,
          data: {
            checkout_url: data.checkout_url,
            amount: data.amount ?? Number(b.price || b.estimated_price || 0),
            customer_name: data.customer_name ?? b.name,
            customer_email: data.customer_email ?? b.email,
            customer_phone: data.customer_phone ?? b.phone,
          },
        });
        toast.success("Payment link copied successfully");
      } else {
        toast.success("Payment link generated");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send link failed");
    } finally { setBusyId(null); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete ${b.name || "this booking"}? This cannot be undone.`)) return;
    setBusyId(b.booking_id);
    try {
      await api.delete(`/admin/pending-payments/${b.booking_id}`);
      toast.success("Booking deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  if (state.loading) {
    return <div className="p-8 flex justify-center" data-testid="pending-payments-loading"><Loader2 className="w-6 h-6 text-gray-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="pending-payments-panel">
      <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-white border-2 border-amber-100 p-4 flex items-center gap-3">
        <DollarSign className="w-8 h-8 text-amber-700" />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-amber-800">Money in motion</div>
          <div className="text-xl font-extrabold text-gray-900 font-[Outfit]" data-testid="pending-payments-summary">
            {state.count} pending · ${state.total_due.toFixed(2)} due
          </div>
        </div>
      </div>

      {state.bookings.length === 0 ? (
        <div className="rounded-2xl bg-white border border-gray-200 p-8 text-center text-sm text-gray-500" data-testid="pending-payments-empty">
          No bookings awaiting payment.
        </div>
      ) : (
        <div className="space-y-3">
          {state.bookings.map((b) => {
            const price = Number(b.price || b.estimated_price || 0);
            const busy = busyId === b.booking_id;
            return (
              <div key={b.booking_id} className="rounded-2xl bg-white border border-gray-200 p-4" data-testid={`pending-row-${b.booking_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-extrabold text-gray-900 font-[Outfit]">{b.name || "Unnamed"}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {b.service_name || b.service_key || "Service"} · {b.preferred_date || "TBD"} · ${price.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[b.email, b.phone, b.formatted_address || b.address].filter(Boolean).join(" · ")}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-amber-700 mt-1">
                      {b.payment_status} · {b.status}
                      {b.reminder_count > 0 && <span className="ml-2 text-gray-500">{b.reminder_count} reminder{b.reminder_count > 1 ? "s" : ""} sent</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => sendReminder(b)} disabled={busy} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white h-8 px-3 text-xs" data-testid={`send-reminder-${b.booking_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Send Payment Reminder
                  </Button>
                  <Button size="sm" onClick={() => sendLink(b)} disabled={busy} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-8 px-3 text-xs" data-testid={`send-link-${b.booking_id}`}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Send Payment Link
                  </Button>
                  {b.phone && (
                    <a href={`sms:${b.phone}`} className="inline-flex items-center gap-1 rounded-full border border-gray-300 hover:bg-gray-50 h-8 px-3 text-xs font-bold text-gray-800" data-testid={`sms-${b.booking_id}`}>
                      <MessageSquare className="w-3.5 h-3.5" /> Text
                    </a>
                  )}
                  {b.email && (
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 rounded-full border border-gray-300 hover:bg-gray-50 h-8 px-3 text-xs font-bold text-gray-800" data-testid={`email-${b.booking_id}`}>
                      <Mail className="w-3.5 h-3.5" /> Email
                    </a>
                  )}
                  {b.phone && (
                    <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 rounded-full border border-gray-300 hover:bg-gray-50 h-8 px-3 text-xs font-bold text-gray-800" data-testid={`call-${b.booking_id}`}>
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  )}
                  <Button size="sm" onClick={() => del(b)} disabled={busy} variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50 h-8 px-3 text-xs" data-testid={`delete-${b.booking_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* iter132c — Quick-share dialog opens after Send Payment Link */}
      <PaymentLinkShareDialog
        open={shareDialog.open}
        data={shareDialog.data}
        onClose={() => setShareDialog({ open: false, data: null })}
      />
    </div>
  );
}
